import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  TrendingUp, 
  DollarSign, 
  FolderKanban, 
  Users,
  ArrowUpRight,
  Clock,
  Timer,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  FileText,
  Wallet,
  Play,
  Activity,
  Calendar,
  Volume2,
  Loader2,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Bell,
  Package,
  CreditCard,
  MoreHorizontal
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import Avatar from '../../components/common/Avatar';
import { getStatusLabel } from '../../lib/statusConfig';
import CalendarPage from '../calendar/CalendarPage';
import QuickActionsFloatingWidget from '../../components/widgets/QuickActionsFloatingWidget';
import QuickNotesWidget from '../../components/widgets/QuickNotesWidget';

const COLORS = ['#00d4aa', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6'];

const AgencyDashboard = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [activeTimers, setActiveTimers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [pendingActions, setPendingActions] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [teamTimeReport, setTeamTimeReport] = useState([]);
  const [summaryHistory, setSummaryHistory] = useState([]);
  const [showSummaryHistory, setShowSummaryHistory] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, timersRes, timelineRes, actionsRes, summaryRes, teamTimeRes] = await Promise.all([
        api.get('/dashboard/agency'),
        api.get('/time/all-active'),
        api.get('/dashboard/agency/timeline?limit=30'),
        api.get('/dashboard/agency/pending-actions'),
        api.get('/dashboard/agency/daily-summary'),
        api.get('/dashboard/agency/team-time').catch(() => ({ data: [] }))
      ]);
      
      setDashboard(dashboardRes.data);
      setActiveTimers(timersRes.data || []);
      setTimeline(timelineRes.data || []);
      setPendingActions(actionsRes.data);
      setDailySummary(summaryRes.data);
      setTeamTimeReport(teamTimeRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummaryHistory = async () => {
    try {
      const res = await api.get('/summaries?days=30');
      setSummaryHistory(res.data || []);
    } catch (error) {
      console.error('Error fetching summary history:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh active timers every 30 seconds
    const interval = setInterval(() => {
      api.get('/time/all-active').then(res => setActiveTimers(res.data || []));
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}min`;
    return `${mins}min`;
  };

  // Format timestamp for French locale with proper timezone
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
  };

  // Format time for French locale
  const formatTimeFR = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  // Get activity icon
  const getActivityIcon = (type, action) => {
    switch (type) {
      case 'time_entry': return <Timer className="w-4 h-4 text-blue-500" />;
      case 'task': return action === 'completed' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Activity className="w-4 h-4 text-purple-500" />;
      case 'project': return <FolderKanban className="w-4 h-4 text-indigo-500" />;
      case 'ticket': return <MessageSquare className="w-4 h-4 text-orange-500" />;
      case 'wallet': return <Wallet className="w-4 h-4 text-emerald-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  // Revenue by type data
  const [revenueByType, setRevenueByType] = useState([]);
  
  useEffect(() => {
    api.get('/dashboard/agency/revenue-by-type')
      .then(res => setRevenueByType(res.data || []))
      .catch(() => {});
  }, []);

  // Pie data from API or defaults
  const pieData = revenueByType.length > 0 ? revenueByType : [
    { name: 'Sites Web', value: 0, color: '#6366f1' },
    { name: 'Maintenance', value: 0, color: '#00d4aa' },
  ];

  // Generate AI Summary - Using new detailed AI endpoint
  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.post('/summaries/generate', { date: today });
      if (res.data?.success) {
        // Update local state with new summary info
        setDailySummary(prev => ({
          ...prev,
          ai_summary: res.data.summary
        }));
        toast.success('Résumé IA détaillé généré !');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingSummary(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header avec date */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Bonjour {user?.first_name} 👋
            </h1>
            <p className="text-sm text-[#64748b]">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#64748b]">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <>
            {/* Row 1: Actions en attente (priorité haute) */}
            {pendingActions && (pendingActions.briefs_pending > 0 || pendingActions.tasks_awaiting_client > 0 || pendingActions.projects_to_deliver > 0 || pendingActions.pending_wallet_topups > 0 || pendingActions.open_tickets > 0) && (
              <Card className="mb-4 border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-50/50 to-transparent">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell className="w-4 h-4 text-amber-500" />
                    <span className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Actions en attente</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pendingActions.briefs_pending > 0 && (
                      <Badge 
                        className="cursor-pointer hover:opacity-80 bg-amber-100 text-amber-700 border-amber-200"
                        onClick={() => navigate('/projects')}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {pendingActions.briefs_pending} brief{pendingActions.briefs_pending > 1 ? 's' : ''} à valider
                      </Badge>
                    )}
                    {pendingActions.tasks_awaiting_client > 0 && (
                      <Badge 
                        className="cursor-pointer hover:opacity-80 bg-orange-100 text-orange-700 border-orange-200"
                        onClick={() => navigate('/projects')}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {pendingActions.tasks_awaiting_client} validation{pendingActions.tasks_awaiting_client > 1 ? 's' : ''} client
                      </Badge>
                    )}
                    {pendingActions.projects_to_deliver > 0 && (
                      <Badge 
                        className="cursor-pointer hover:opacity-80 bg-purple-100 text-purple-700 border-purple-200"
                        onClick={() => navigate('/projects')}
                      >
                        <Package className="w-3 h-3 mr-1" />
                        {pendingActions.projects_to_deliver} à livrer
                      </Badge>
                    )}
                    {pendingActions.pending_wallet_topups > 0 && (
                      <Badge 
                        className="cursor-pointer hover:opacity-80 bg-emerald-100 text-emerald-700 border-emerald-200"
                        onClick={() => navigate('/wallet-management')}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        {pendingActions.pending_wallet_topups} recharge{pendingActions.pending_wallet_topups > 1 ? 's' : ''} wallet
                      </Badge>
                    )}
                    {pendingActions.open_tickets > 0 && (
                      <Badge 
                        className="cursor-pointer hover:opacity-80 bg-red-100 text-red-700 border-red-200"
                        onClick={() => navigate('/tickets')}
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {pendingActions.open_tickets} ticket{pendingActions.open_tickets > 1 ? 's' : ''} ouvert{pendingActions.open_tickets > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Row 2: KPIs compacts + Chronos actifs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {/* KPI: Projets */}
              <Card className="card card-hover cursor-pointer" onClick={() => navigate('/projects')}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <FolderKanban className="w-4 h-4" style={{ color: theme.colors?.accent }} />
                    <span className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                      {dashboard?.active_projects || 0}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">Projets actifs</p>
                </CardContent>
              </Card>

              {/* KPI: Clients */}
              <Card className="card card-hover cursor-pointer" onClick={() => navigate('/clients')}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <Users className="w-4 h-4 text-amber-500" />
                    <span className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                      {dashboard?.total_clients || 0}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">Clients</p>
                </CardContent>
              </Card>

              {/* KPI: CA mois */}
              <Card className="card">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    <span className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                      {((dashboard?.total_revenue || 0) / 1000).toFixed(1)}k€
                    </span>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">CA ce mois</p>
                </CardContent>
              </Card>

              {/* KPI: Marge */}
              <Card className="card">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <TrendingUp className="w-4 h-4 text-indigo-500" />
                    <span className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                      {((dashboard?.total_margin || 0) / 1000).toFixed(1)}k€
                    </span>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">Marge</p>
                </CardContent>
              </Card>

              {/* KPI: Chronos */}
              <Card className={cn("card", activeTimers.length > 0 && "border-red-200 bg-red-50/30")}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <Timer className="w-4 h-4 text-red-500" />
                    <span className="text-lg font-bold" style={{ color: activeTimers.length > 0 ? '#dc2626' : theme.colors?.primary }}>
                      {activeTimers.length}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">Chronos actifs</p>
                </CardContent>
              </Card>
            </div>

            {/* Row 3: Chronos détaillés si actifs */}
            {activeTimers.length > 0 && (
              <Card className="mb-4 border-l-4" style={{ borderLeftColor: theme.colors?.accent }}>
                <CardContent className="py-3 px-4">
                  <div className="flex flex-wrap gap-3">
                    {Object.values(
                      activeTimers.reduce((acc, timer) => {
                        const key = timer.user_id || timer.user_name;
                        if (!acc[key]) {
                          acc[key] = { ...timer, timer_count: 1, total_duration: timer.duration_seconds || 0 };
                        } else {
                          acc[key].timer_count += 1;
                          acc[key].total_duration += timer.duration_seconds || 0;
                        }
                        return acc;
                      }, {})
                    ).map((timer) => (
                      <div key={timer.user_id || timer.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100">
                        <Avatar name={timer.user_name} size="xs" isActive={true} />
                        <span className="text-xs font-medium">{timer.user_name}</span>
                        <Badge variant="outline" className="font-mono text-[10px] h-5 border-indigo-200 text-indigo-600">
                          {formatDuration(timer.total_duration || timer.duration_seconds)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Full Calendar for Account Managers - with all features */}
            {(user?.role === 'project_manager' || user?.role === 'customer_service' || user?.role === 'agency') && (
              <div className="mb-6">
                <CalendarPage embedded={true} />
              </div>
            )}

            {/* Quick Notes Widget */}
            <div className="mb-6">
              <QuickNotesWidget maxNotes={6} showTitle={true} />
            </div>

            {/* Main Content - Timeline + Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Timeline */}
              <Card className="card lg:col-span-2">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                      Timeline en temps réel
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">Aujourd'hui</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    {timeline.length === 0 ? (
                      <p className="text-center text-[#64748b] py-8">Aucune activité aujourd'hui</p>
                    ) : (
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
                        
                        <div className="space-y-4">
                          {timeline.map((activity, idx) => (
                            <div key={activity.id || idx} className="relative flex gap-4 pl-10">
                              {/* Timeline dot */}
                              <div className="absolute left-2 w-4 h-4 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                                {getActivityIcon(activity.type, activity.action)}
                              </div>
                              
                              <div className="flex-1 pb-4">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    {activity.user_name && (
                                      <span className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                                        {activity.user_name}
                                      </span>
                                    )}
                                    <p className="text-sm text-[#64748b]">{activity.description}</p>
                                    {/* Détails supplémentaires: projet et tâche */}
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {activity.project_name && (
                                        <span className="text-xs text-[#94a3b8] flex items-center gap-1">
                                          📁 {activity.project_name}
                                        </span>
                                      )}
                                      {activity.task_name && (
                                        <span className="text-xs text-indigo-500 flex items-center gap-1">
                                          ✓ {activity.task_name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-xs text-[#94a3b8] whitespace-nowrap">
                                    {formatTimestamp(activity.timestamp)}
                                  </span>
                                </div>
                                
                                {/* Metadata badges */}
                                {activity.metadata && (
                                  <div className="flex gap-2 mt-2">
                                    {activity.metadata.duration_minutes && (
                                      <Badge variant="outline" className="text-xs">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {activity.metadata.duration_minutes.toFixed(0)} min
                                      </Badge>
                                    )}
                                    {activity.metadata.earnings > 0 && (
                                      <Badge variant="outline" className="text-xs text-emerald-600">
                                        +{activity.metadata.earnings.toFixed(2)}€
                                      </Badge>
                                    )}
                                    {activity.metadata.status && (
                                      <Badge variant="outline" className="text-xs">
                                        {getStatusLabel(activity.metadata.status)}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Right Column - AI Summary + Charts */}
              <div className="space-y-6">
                {/* AI Daily Summary */}
                <Card className="card border-2" style={{ borderColor: `${theme.colors?.accent}40` }}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                        Résumé IA de la journée
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/summaries')}
                        className="text-xs gap-1 h-7"
                      >
                        Historique
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dailySummary?.stats ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="p-2 rounded-lg bg-slate-50">
                            <p className="text-xs text-[#64748b]">Heures travaillées</p>
                            <p className="font-bold" style={{ color: theme.colors?.primary }}>{dailySummary.stats.total_hours}h</p>
                          </div>
                          <div className="p-2 rounded-lg bg-slate-50">
                            <p className="text-xs text-[#64748b]">Tâches terminées</p>
                            <p className="font-bold" style={{ color: theme.colors?.primary }}>{dailySummary.stats.tasks_completed}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-slate-50">
                            <p className="text-xs text-[#64748b]">Membres actifs</p>
                            <p className="font-bold" style={{ color: theme.colors?.primary }}>{dailySummary.stats.active_workers}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-slate-50">
                            <p className="text-xs text-[#64748b]">Revenus wallet</p>
                            <p className="font-bold text-emerald-600">{dailySummary.stats.wallet_revenue}€</p>
                          </div>
                        </div>
                        
                        {dailySummary.summary_text && (
                          <div className="p-3 rounded-lg bg-gradient-to-r from-slate-50 to-white border">
                            <p className="text-sm text-[#334155]">{dailySummary.summary_text}</p>
                          </div>
                        )}
                        
                        {dailySummary.audio_url && (
                          <Button variant="outline" size="sm" className="w-full gap-2">
                            <Volume2 className="w-4 h-4" />
                            Écouter le résumé
                          </Button>
                        )}
                        
                        {/* Button to generate detailed AI summary */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={generateSummary}
                          disabled={generatingSummary}
                          className="w-full gap-2"
                        >
                          {generatingSummary ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Génération du résumé détaillé...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" style={{ color: theme.colors?.accent }} />
                              Générer résumé IA détaillé
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-[#64748b] mb-3">Pas encore de résumé pour aujourd'hui</p>
                        <Button 
                          size="sm" 
                          onClick={generateSummary}
                          disabled={generatingSummary}
                          style={{ backgroundColor: theme.colors?.accent }}
                          className="text-white"
                        >
                          {generatingSummary ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Génération...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Générer le résumé
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Revenue by Type Chart */}
                <Card className="card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">CA par type de projet</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pieData.length > 0 && pieData.some(d => d.value > 0) ? (
                      <>
                        <div className="h-40">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={55}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')}€`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 justify-center">
                          {pieData.map((entry, index) => (
                            <div key={entry.name} className="flex items-center gap-1.5">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: entry.color || COLORS[index % COLORS.length] }}
                              />
                              <span className="text-xs text-[#64748b]">{entry.name}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-center text-[#64748b] py-8 text-sm">Aucune donnée ce mois</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Team Time Report */}
            <Card className="card mt-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  Temps de travail équipe - Aujourd'hui
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teamTimeReport.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {teamTimeReport.map((member) => (
                      <div 
                        key={member.user_id}
                        className={cn(
                          "p-4 rounded-xl border transition-all",
                          member.is_working 
                            ? "border-green-200 bg-green-50/50" 
                            : "border-slate-100 bg-slate-50/50"
                        )}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar name={member.name} size="md" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate" style={{ color: theme.colors?.primary }}>
                              {member.name}
                            </p>
                            {member.is_working && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse" />
                                En travail
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-white rounded-lg">
                            <p className="text-xs text-[#64748b]">Temps</p>
                            <p className="font-bold text-lg" style={{ color: theme.colors?.primary }}>
                              {member.hours_today}h
                            </p>
                          </div>
                          <div className="text-center p-2 bg-white rounded-lg">
                            <p className="text-xs text-[#64748b]">Gains</p>
                            <p className="font-bold text-lg" style={{ color: theme.colors?.accent }}>
                              {member.earnings_today}€
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-[#64748b]">
                          <span>{member.tasks_completed} tâches</span>
                          <span>{member.projects_worked} projets</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#64748b] py-8">Aucune donnée de temps aujourd'hui</p>
                )}
              </CardContent>
            </Card>

            {/* Revenue Chart Full Width */}
            <Card className="card mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Évolution du CA, Coûts & Marge</CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-slate-600">CA</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <span className="text-slate-600">Coûts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-gradient-to-b from-sky-200 to-sky-400" />
                      <span className="text-slate-600">Marge</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboard?.monthly_revenue || []}>
                      <defs>
                        {/* Gradient for the margin area - light blue for positive margin */}
                        <linearGradient id="colorMarginArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.7}/>
                          <stop offset="100%" stopColor="#7dd3fc" stopOpacity={0.3}/>
                        </linearGradient>
                        {/* Gradient for revenue */}
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.2}/>
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#64748b" 
                        fontSize={12}
                        tickFormatter={(value) => {
                          const [year, month] = value.split('-');
                          const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                          return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
                        }}
                      />
                      <YAxis 
                        stroke="#64748b" 
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const [year, month] = label.split('-');
                            const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                            const revenue = payload.find(p => p.dataKey === 'revenue')?.value || 0;
                            const costs = payload.find(p => p.dataKey === 'costs')?.value || 0;
                            const margin = revenue - costs;
                            
                            return (
                              <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                                <p className="font-semibold text-slate-700 mb-2">{months[parseInt(month) - 1]} {year}</p>
                                <div className="space-y-1 text-sm">
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full bg-emerald-500" />
                                      CA
                                    </span>
                                    <span className="font-semibold text-emerald-600">{revenue.toLocaleString('fr-FR')}€</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full bg-red-500" />
                                      Dépenses
                                    </span>
                                    <span className="font-semibold text-red-500">{costs.toLocaleString('fr-FR')}€</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-4 pt-1 border-t border-slate-200">
                                    <span className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded bg-gradient-to-b from-sky-300 to-sky-500" />
                                      Marge
                                    </span>
                                    <span className={`font-bold ${margin >= 0 ? 'text-sky-600' : 'text-red-600'}`}>
                                      {margin >= 0 ? '+' : ''}{margin.toLocaleString('fr-FR')}€
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      {/* Revenue area - light green background */}
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#10b981" 
                        strokeWidth={3}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                        name="CA"
                      />
                      {/* Margin area - shows the difference between revenue and costs */}
                      <Area 
                        type="monotone" 
                        dataKey="margin" 
                        stroke="#0ea5e9" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        fillOpacity={1} 
                        fill="url(#colorMarginArea)" 
                        name="Marge"
                        baseLine={0}
                      />
                      {/* Costs line - red, no fill */}
                      <Area 
                        type="monotone" 
                        dataKey="costs" 
                        stroke="#ef4444" 
                        strokeWidth={3}
                        fillOpacity={0} 
                        fill="transparent" 
                        name="Dépenses"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-1 bg-emerald-500 rounded" />
                    <span className="text-slate-600">CA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-1 bg-red-500 rounded" />
                    <span className="text-slate-600">Dépenses</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-gradient-to-b from-sky-300 to-sky-100 rounded" />
                    <span className="text-slate-600">Marge (zone colorée)</span>
                  </div>
                </div>
                {/* Summary stats below chart */}
                {dashboard?.monthly_revenue && dashboard.monthly_revenue.length > 0 && (
                  <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">CA Total (6 mois)</p>
                      <p className="text-lg font-bold text-emerald-600">
                        {dashboard.monthly_revenue.reduce((sum, m) => sum + (m.revenue || 0), 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Dépenses Total</p>
                      <p className="text-lg font-bold text-red-500">
                        {dashboard.monthly_revenue.reduce((sum, m) => sum + (m.costs || 0), 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Marge Totale</p>
                      <p className="text-lg font-bold text-sky-600">
                        {dashboard.monthly_revenue.reduce((sum, m) => sum + (m.margin || 0), 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
      
      {/* Floating Action Button */}
      <QuickActionsFloatingWidget />
    </div>
  );
};

export default AgencyDashboard;
